/**
 * Hover-card style coord entry (no dim overlay) — anchored near the pick point on the canvas host.
 * Reuses validation logic from coord-entry-modal fields.
 */

import { formatCoordDecimalForNumericInput } from '../tools/coord-marker-entity';

function parseNum(v: string, fallback = 0): number {
    const s = String(v ?? '').trim();
    if (s === '') return fallback;
    const n = Number(s);
    return Number.isFinite(n) ? n : NaN;
}

function dmsToDecimal(deg: string, min: string, sec: string, ref: string): number | null {
    const d = String(deg ?? '').trim();
    const m = String(min ?? '').trim();
    const s = String(sec ?? '').trim();
    if (d === '' && m === '' && s === '') return null;
    const dv = d === '' ? 0 : Number(d);
    const mv = m === '' ? 0 : Number(m);
    const sv = s === '' ? 0 : Number(s);
    if (!Number.isFinite(dv) || !Number.isFinite(mv) || !Number.isFinite(sv)) return null;
    let dec = Math.abs(dv) + mv / 60 + sv / 3600;
    const r = String(ref || '').toUpperCase();
    if (r === 'S' || r === 'W') dec = -dec;
    return dec;
}

function clampLatLng(lat: number, lng: number): { lat: number; lng: number } | null {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng };
}

export type CoordEntryResult = {
    lat: number;
    lng: number;
    ele?: number;
};

export type CoordEntryHoverAnchor = {
    /** Usually coord-markers-host — position relative; card must receive pointer-events. */
    host: HTMLElement;
    clientX: number;
    clientY: number;
};

export type CoordEntryHoverCardOpts = CoordEntryHoverAnchor & {
    /** Renders title “Ground control point #N”. */
    gcpIndex?: number;
    /** Hint above lat/lng fields for map-plot pairing. */
    awaitingMapHint?: boolean;
    /**
     * User clicked “Pick from map” — arm host mini-map and register setter for one click.
     */
    onArmMapPick?: (setLatLng: (lat: number, lng: number) => void) => void;
};

function decimalToDmsParts(deg: number, kind: 'lat' | 'lng'): { d: number; m: number; s: number; ref: string } {
    const abs = Math.abs(deg);
    const d = Math.floor(abs);
    const mFloat = (abs - d) * 60;
    const m = Math.floor(mFloat);
    const s = (mFloat - m) * 60;
    const ref = kind === 'lat' ? (deg >= 0 ? 'N' : 'S') : deg >= 0 ? 'E' : 'W';
    return { d, m, s, ref };
}

let coordEntryCardSession = false;
let activeHoverCardCancel: (() => void) | null = null;

/** True while the GCP / coord hover card dialog is open (avoid map pill vs card conflict). */
export function isCoordEntryHoverCardOpen(): boolean {
    return coordEntryCardSession;
}

/** Cancels the open coord entry hover card (same as Cancel), e.g. when starting a replacement GCP pick. */
export function cancelActiveCoordEntryHoverIfAny(): void {
    try {
        activeHoverCardCancel?.();
    } catch {
        /* ignore */
    }
}

function positionCardNearClick(host: HTMLElement, card: HTMLElement, clientX: number, clientY: number): void {
    const hostRect = host.getBoundingClientRect();
    const mx = clientX - hostRect.left;
    const my = clientY - hostRect.top;
    const pad = 10;
    const gap = 12;

    card.style.position = 'absolute';
    card.style.margin = '0';
    card.style.left = '0';
    card.style.top = '0';
    card.style.zIndex = '40';

    const cw = card.offsetWidth;
    const ch = card.offsetHeight;

    let left = mx - cw / 2;
    let top = my - ch - gap;

    left = Math.min(Math.max(left, pad), Math.max(pad, hostRect.width - cw - pad));
    if (top < pad) {
        top = my + gap;
    }
    top = Math.min(Math.max(top, pad), Math.max(pad, hostRect.height - ch - pad));

    card.style.left = `${left}px`;
    card.style.top = `${top}px`;
}

/**
 * Open floating coord entry (annotation-hover-card look). Closes on Esc or outside click (not on card).
 */
export function openCoordEntryHoverCard(opts: CoordEntryHoverCardOpts): Promise<CoordEntryResult | null> {
    if (coordEntryCardSession) {
        cancelActiveCoordEntryHoverIfAny();
    }
    if (coordEntryCardSession) {
        return Promise.resolve(null);
    }
    coordEntryCardSession = true;

    return new Promise((resolve) => {
        const { host, clientX, clientY, gcpIndex, awaitingMapHint, onArmMapPick } = opts;
        let settled = false;

        const card = document.createElement('div');
        card.className = 'annotation-hover-card coord-entry-hover-card';
        card.setAttribute('role', 'dialog');
        card.setAttribute('aria-modal', 'true');
        card.setAttribute('aria-labelledby', 'coord-entry-card-title');

        const header = document.createElement('div');
        header.className = 'coord-entry-card-header coord-entry-card-header--with-close';

        const title = document.createElement('div');
        title.id = 'coord-entry-card-title';
        title.className = 'coord-entry-card-title';
        title.textContent =
            typeof gcpIndex === 'number' && Number.isFinite(gcpIndex) && gcpIndex > 0
                ? `Ground control point #${gcpIndex}`
                : 'Ground control point';

        const headerClose = document.createElement('button');
        headerClose.type = 'button';
        headerClose.className = 'coord-entry-card-close';
        headerClose.setAttribute('aria-label', 'Delete pending ground control point');
        headerClose.textContent = '\u00d7';

        header.appendChild(title);
        header.appendChild(headerClose);

        const body = document.createElement('div');
        body.className = 'coord-entry-card-body';

        if (awaitingMapHint) {
            const hint = document.createElement('p');
            hint.className = 'coord-entry-map-hint';
            hint.textContent = 'Press Pick from map, then click the satellite map to fill coordinates — or type below.';
            body.appendChild(hint);
        }

        const decToggle = document.createElement('input');
        decToggle.type = 'checkbox';
        const decLabel = document.createElement('span');
        decLabel.className = 'coord-entry-decimal-label';
        decLabel.textContent = 'Decimal degrees';

        const dmsWrap = document.createElement('div');
        dmsWrap.className = 'coord-entry-dms-grid';

        const makeDmsRow = (label: string, refId: 'lat' | 'lng') => {
            const field = document.createElement('div');
            field.className = 'annotation-modal-field';
            const lab = document.createElement('span');
            lab.className = 'annotation-modal-label';
            lab.textContent = label;
            const row = document.createElement('div');
            row.className = 'coord-entry-dms-row';
            const deg = document.createElement('input');
            deg.type = 'number';
            deg.className = 'annotation-modal-input coord-entry-input-compact';
            deg.placeholder = 'deg';
            const min = document.createElement('input');
            min.type = 'number';
            min.className = 'annotation-modal-input coord-entry-input-compact';
            min.placeholder = 'min';
            const sec = document.createElement('input');
            sec.type = 'number';
            sec.step = 'any';
            sec.className = 'annotation-modal-input coord-entry-input-compact';
            sec.placeholder = 'sec';
            const ref = document.createElement('select');
            ref.className = 'coord-entry-ref-select';
            ref.setAttribute('aria-label', refId === 'lat' ? 'North or South' : 'East or West');
            if (refId === 'lat') {
                ref.innerHTML = '<option value="N">N</option><option value="S">S</option>';
            } else {
                ref.innerHTML = '<option value="E">E</option><option value="W">W</option>';
            }
            row.appendChild(deg);
            row.appendChild(min);
            row.appendChild(sec);
            row.appendChild(ref);
            field.appendChild(lab);
            field.appendChild(row);
            return { field, deg, min, sec, ref };
        };

        const latDms = makeDmsRow('Latitude', 'lat');
        const lngDms = makeDmsRow('Longitude', 'lng');
        dmsWrap.appendChild(latDms.field);
        dmsWrap.appendChild(lngDms.field);

        const decWrap = document.createElement('div');
        decWrap.className = 'coord-entry-dec-grid';
        decWrap.style.display = 'none';
        const latField = document.createElement('div');
        latField.className = 'annotation-modal-field';
        const latLab = document.createElement('span');
        latLab.className = 'annotation-modal-label';
        latLab.textContent = 'Latitude';
        const latDecRow = document.createElement('div');
        latDecRow.className = 'coord-entry-dec-row-inline';
        const latDec = document.createElement('input');
        latDec.type = 'number';
        latDec.step = 'any';
        latDec.className = 'annotation-modal-input';
        latDec.placeholder = 'Decimal latitude (° N/S)';
        const latHemi = document.createElement('span');
        latHemi.className = 'coord-entry-dec-hemi';
        latHemi.setAttribute('aria-hidden', 'true');
        latDecRow.appendChild(latDec);
        latDecRow.appendChild(latHemi);
        latField.appendChild(latLab);
        latField.appendChild(latDecRow);

        const lngField = document.createElement('div');
        lngField.className = 'annotation-modal-field';
        const lngLab = document.createElement('span');
        lngLab.className = 'annotation-modal-label';
        lngLab.textContent = 'Longitude';
        const lngDecRow = document.createElement('div');
        lngDecRow.className = 'coord-entry-dec-row-inline';
        const lngDec = document.createElement('input');
        lngDec.type = 'number';
        lngDec.step = 'any';
        lngDec.className = 'annotation-modal-input';
        lngDec.placeholder = 'Decimal longitude (° E/W)';
        const lngHemi = document.createElement('span');
        lngHemi.className = 'coord-entry-dec-hemi';
        lngHemi.setAttribute('aria-hidden', 'true');
        lngDecRow.appendChild(lngDec);
        lngDecRow.appendChild(lngHemi);
        lngField.appendChild(lngLab);
        lngField.appendChild(lngDecRow);
        decWrap.appendChild(latField);
        decWrap.appendChild(lngField);

        const eleField = document.createElement('div');
        eleField.className = 'annotation-modal-field';
        const eleLab = document.createElement('span');
        eleLab.className = 'annotation-modal-label';
        eleLab.textContent = 'Elevation (optional)';
        const eleRowInline = document.createElement('div');
        eleRowInline.className = 'coord-entry-ele-inline';
        const eleIn = document.createElement('input');
        eleIn.type = 'number';
        eleIn.step = 'any';
        eleIn.className = 'annotation-modal-input coord-entry-ele-input';
        eleIn.placeholder = 'e.g. 123.4';
        const eleUnit = document.createElement('select');
        eleUnit.className = 'coord-entry-unit-select';
        eleUnit.setAttribute('aria-label', 'Elevation unit');
        eleUnit.innerHTML = '<option value="m">m</option><option value="ft">ft</option>';
        eleRowInline.appendChild(eleIn);
        eleRowInline.appendChild(eleUnit);
        eleField.appendChild(eleLab);
        eleField.appendChild(eleRowInline);

        const err = document.createElement('div');
        err.className = 'coord-entry-error';
        err.style.display = 'none';

        const footer = document.createElement('div');
        footer.className = 'coord-entry-card-footer';
        const cancel = document.createElement('button');
        cancel.type = 'button';
        cancel.className = 'annotation-modal-btn';
        cancel.textContent = 'Cancel';
        const pickMap = document.createElement('button');
        pickMap.type = 'button';
        pickMap.className = 'annotation-modal-btn coord-entry-pick-map-btn';
        pickMap.textContent = 'Pick from map';
        const ok = document.createElement('button');
        ok.type = 'button';
        ok.className = 'annotation-modal-btn annotation-modal-btn-primary';
        ok.textContent = 'Confirm';
        ok.disabled = true;
        const toggleRow = document.createElement('label');
        toggleRow.className = 'coord-entry-toggle-row';
        toggleRow.appendChild(decToggle);
        toggleRow.appendChild(decLabel);

        body.appendChild(dmsWrap);
        body.appendChild(decWrap);
        body.appendChild(eleField);
        body.appendChild(err);
        footer.appendChild(cancel);
        footer.appendChild(pickMap);
        footer.appendChild(toggleRow);
        footer.appendChild(ok);

        card.appendChild(header);
        card.appendChild(body);
        card.appendChild(footer);
        host.appendChild(card);

        const relayout = () => positionCardNearClick(host, card, clientX, clientY);

        const syncDecHemi = (): void => {
            const la = parseNum(latDec.value, NaN);
            const lo = parseNum(lngDec.value, NaN);
            latHemi.textContent = Number.isFinite(la) ? (la >= 0 ? 'N' : 'S') : '';
            lngHemi.textContent = Number.isFinite(lo) ? (lo >= 0 ? 'E' : 'W') : '';
        };

        const readLatLngValid = (): boolean => {
            let lat: number;
            let lng: number;
            if (decToggle.checked) {
                lat = parseNum(latDec.value, NaN);
                lng = parseNum(lngDec.value, NaN);
            } else {
                const la = dmsToDecimal(latDms.deg.value, latDms.min.value, latDms.sec.value, latDms.ref.value);
                const lo = dmsToDecimal(lngDms.deg.value, lngDms.min.value, lngDms.sec.value, lngDms.ref.value);
                if (la === null || lo === null) return false;
                lat = la;
                lng = lo;
            }
            return clampLatLng(lat, lng) !== null;
        };

        const syncConfirmEnabled = (): void => {
            ok.disabled = !readLatLngValid();
        };

        const flashPrefillTargets = (): HTMLElement[] => {
            const out: HTMLElement[] = [];
            if (decToggle.checked) {
                out.push(latDec, lngDec);
            } else {
                out.push(latDms.deg, latDms.min, latDms.sec, latDms.ref);
                out.push(lngDms.deg, lngDms.min, lngDms.sec, lngDms.ref);
            }
            return out.filter(Boolean);
        };

        const setLatLngFromMap = (lat: number, lng: number): void => {
            const cl = clampLatLng(lat, lng);
            if (!cl) return;
            if (decToggle.checked) {
                latDec.value = formatCoordDecimalForNumericInput(cl.lat);
                lngDec.value = formatCoordDecimalForNumericInput(cl.lng);
                syncDecHemi();
            } else {
                const la = decimalToDmsParts(cl.lat, 'lat');
                const lo = decimalToDmsParts(cl.lng, 'lng');
                latDms.deg.value = String(la.d);
                latDms.min.value = String(la.m);
                latDms.sec.value = String(la.s);
                latDms.ref.value = la.ref;
                lngDms.deg.value = String(lo.d);
                lngDms.min.value = String(lo.m);
                lngDms.sec.value = String(lo.s);
                lngDms.ref.value = lo.ref;
            }
            for (const el of flashPrefillTargets()) {
                el.classList.remove('coord-entry-prefill-flash');
                void el.offsetWidth;
                el.classList.add('coord-entry-prefill-flash');
            }
            window.setTimeout(() => {
                for (const el of flashPrefillTargets()) {
                    el.classList.remove('coord-entry-prefill-flash');
                }
            }, 900);
            syncConfirmEnabled();
            requestAnimationFrame(() => relayout());
        };

        function finish(v: CoordEntryResult | null): void {
            if (settled) return;
            settled = true;
            activeHoverCardCancel = null;
            coordEntryCardSession = false;
            window.removeEventListener('resize', relayout);
            document.removeEventListener('pointerdown', onDocPointerDown, true);
            window.removeEventListener('keydown', onKey);
            try {
                card.remove();
            } catch {
                /* ignore */
            }
            resolve(v);
        }

        activeHoverCardCancel = () => finish(null);

        function onDocPointerDown(e: PointerEvent): void {
            const t = e.target as Node | null;
            if (t && card.contains(t)) return;
            finish(null);
        }

        const validateAndCommit = (): boolean => {
            err.style.display = 'none';
            err.textContent = '';
            let lat: number;
            let lng: number;
            if (decToggle.checked) {
                lat = parseNum(latDec.value, NaN);
                lng = parseNum(lngDec.value, NaN);
            } else {
                const la = dmsToDecimal(latDms.deg.value, latDms.min.value, latDms.sec.value, latDms.ref.value);
                const lo = dmsToDecimal(lngDms.deg.value, lngDms.min.value, lngDms.sec.value, lngDms.ref.value);
                if (la === null || lo === null) {
                    err.textContent = 'Enter latitude and longitude.';
                    err.style.display = 'block';
                    requestAnimationFrame(() => relayout());
                    return false;
                }
                lat = la;
                lng = lo;
            }
            const cl = clampLatLng(lat, lng);
            if (!cl) {
                err.textContent = 'Latitude must be −90…90° and longitude −180…180°.';
                err.style.display = 'block';
                requestAnimationFrame(() => relayout());
                return false;
            }
            let ele: number | undefined;
            const es = String(eleIn.value ?? '').trim();
            if (es !== '') {
                const en = Number(es);
                if (!Number.isFinite(en)) {
                    err.textContent = 'Elevation must be a number.';
                    err.style.display = 'block';
                    requestAnimationFrame(() => relayout());
                    return false;
                }
                const u = eleUnit.value === 'ft' ? 'ft' : 'm';
                ele = u === 'ft' ? en * 0.3048 : en;
            }
            finish({ lat: cl.lat, lng: cl.lng, ...(ele !== undefined ? { ele } : {}) });
            return true;
        };

        function onKey(e: KeyboardEvent): void {
            if (e.key === 'Escape') {
                e.preventDefault();
                finish(null);
            } else if (e.key === 'Enter' && (e.target as HTMLElement)?.tagName !== 'TEXTAREA') {
                const ae = document.activeElement;
                if (card.contains(ae) && !ok.disabled) {
                    e.preventDefault();
                    validateAndCommit();
                }
            }
        }

        headerClose.addEventListener('click', () => finish(null));
        cancel.addEventListener('click', () => finish(null));
        pickMap.addEventListener('click', (e) => {
            e.stopPropagation();
            try {
                onArmMapPick?.(setLatLngFromMap);
            } catch {
                /* ignore */
            }
        });
        ok.addEventListener('click', () => {
            validateAndCommit();
        });

        decToggle.addEventListener('change', () => {
            const useDec = decToggle.checked;
            dmsWrap.style.display = useDec ? 'none' : 'flex';
            decWrap.style.display = useDec ? 'flex' : 'none';
            if (useDec) syncDecHemi();
            syncConfirmEnabled();
            requestAnimationFrame(() => relayout());
        });

        const onFieldInput = () => syncConfirmEnabled();
        latDec.addEventListener('input', () => {
            syncDecHemi();
            onFieldInput();
        });
        lngDec.addEventListener('input', () => {
            syncDecHemi();
            onFieldInput();
        });
        eleIn.addEventListener('input', onFieldInput);
        [latDms.deg, latDms.min, latDms.sec, latDms.ref, lngDms.deg, lngDms.min, lngDms.sec, lngDms.ref].forEach((el) => {
            el.addEventListener('input', onFieldInput);
            el.addEventListener('change', onFieldInput);
        });

        ['pointerdown', 'pointerup', 'pointermove', 'wheel'].forEach((n) => {
            card.addEventListener(n, e => e.stopPropagation());
        });

        document.addEventListener('pointerdown', onDocPointerDown, true);
        window.addEventListener('keydown', onKey);
        window.addEventListener('resize', relayout);

        requestAnimationFrame(() => {
            relayout();
            requestAnimationFrame(relayout);
            syncConfirmEnabled();
            if (decToggle.checked) {
                syncDecHemi();
                latDec.focus();
            } else latDms.deg.focus();
        });
    });
}
